import React, { useState, useEffect, useMemo, useCallback } from "react";
import { HeaderPage } from "../base/headerPage";
import { getUserCredential } from "../../lib/cookies";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getAdminTimesheets, getExportAdminTimesheets } from "./services/getAdminTimesheets";
import { getAllSpecialty } from "./services/getAllSpecialty";
import { getAllPayRequestType } from "./services/getAllPayRequestType";
import { getAllProviders } from "./services/getAllProviders";
import { insertTimesheetApproval } from "./services/insertTimesheetApproval";
import { getEligibleTransactionCount } from "./services/getEligibleTransactionCount";
import { processEventTransactions } from "./services/processEventTransactions";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { LoadingPanel } from "../../components/loadingPanel";
import DataTable from "../../components/dataTable";
import { getSelectedState, GridColumn as Column } from "@progress/kendo-react-grid";
import { DEFAULT_SORT } from "../../constants/grid";
import { columnMenuProps } from "../../components/customColumnMenu";
import { applyDateFormat, getfilterDataString } from "../../utils/utils";
import { getTenantCookieData } from "../../lib/cookies";
import { getGridHeight } from "../../utils/utils";
import { Skeleton } from "@progress/kendo-react-indicators";
import useColumnReorder from "../../custom-hooks/useColumnReorder";
import { getter } from "@progress/kendo-react-common";
import { filterBy } from "@progress/kendo-data-query";
import PayRequestDetailed from "./components/PayRequestDetailed";
import { convertHexToRgba } from "../../utils/utils";
import { getCompAdminStatuses } from "src/components/StatusBadge/status";
import { GetSystemConfigDataByCode } from "src/lib/common-service";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { DialogComponent } from "src/components/dialog";
import { setNewMessage } from "src/redux/actions/setting-actions";
import { getUserTimezone } from "../providerPayRequestDashboard/utils/dateUtils";
import { formatTimesheetQuantity } from "../providerPayRequestDashboard/utils/thresholdQuantity";
import { convertTimeZone, formatServiceDate } from "src/lib/date-time.util";
import "./index.scss";

// Global reference for checkbox change handler
let globalCellCheckboxOnChange = null;
let globalHasWriteAccess = true;

// Helper function to parse date string and create a normalized Date object
const parseAndNormalizeDate = (dateString) => {
  if (!dateString) return null;

  // Extract date part (YYYY-MM-DD) from string like "2025-10-15 00:00:00"
  const datePart = dateString.split(" ")[0];
  const [year, month, day] = datePart.split("-").map((num) => parseInt(num, 10));

  // Create date at midnight in local timezone
  return new Date(year, month - 1, day);
};

// Checkbox Cell Component - Only status 5 (PENDING COMP ADMIN REVIEW) can be selected when write access exists
const CheckboxCell = React.memo((props) => {
  const { dataItem, field, render } = props;
  const isDisabled =
    !globalHasWriteAccess || dataItem.status?.timesheet_status_mstr_key !== 5;

  const handleChange = (e) => {
    if (globalCellCheckboxOnChange && typeof globalCellCheckboxOnChange === "function") {
      globalCellCheckboxOnChange(e, props);
    }
  };

  const defaultRendering = (
    <td className="k-table-td">
      <Checkbox
        value={dataItem[field] === 1}
        onChange={handleChange}
        disabled={isDisabled}
        name={`checkbox_${dataItem.timesheet_mstr_key}`}
      />
    </td>
  );

  return render?.call(undefined, defaultRendering, props) || defaultRendering;
});

// Default Cell Component with tooltip
const DefaultCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val = dataItem[field || ""] ?? "";

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex} className="k-table-td">
      <span title={val}>{val}</span>
    </td>
  );
});

// Custom Status Cell Renderer Component
const StatusCell = (props) => {
  const status = props.dataItem.status;
  const statusData = status.display_name;
  const statusColor = status.color_code;
  const statusColorRgba = convertHexToRgba(statusColor, 0.1);
  const statusColorStyle = {
    backgroundColor: statusColorRgba,
    borderColor: statusColor,
    color: statusColor,
  };

  return (
    <td className="k-table-td" style={{ textAlign: "center", verticalAlign: "middle" }}>
      <span style={statusColorStyle} className="status-badge">
        {statusData}
      </span>
    </td>
  );
};

const BooleanCell = (props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val = dataItem[field] !== "" ? (dataItem[field] === 1 ? "True" : "False") : "";

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex} className="k-table-td">
      <span title={val}>{val}</span>
    </td>
  );
};

// Component to display selected pay requests in confirmation popup
const SelectedPayRequestsList = ({ selectedItems, maxVisible = 10 }) => {
  const visibleItems = Array.from(selectedItems.values());

  const totalBudget = visibleItems.reduce((sum, item) => sum + Number(item.budget || 0), 0);

  return (
    <div className="selected-pay-requests-list">
      <div className="selection-header">
        <span className="timesheet-id">ID</span>
        <span className="provider-name">Provider</span>
        <span className="ts-date">Service Date</span>
        <span className="budget">Amount</span>
      </div>
      <div className="selection-items-scroll">
        {visibleItems.map((item) => (
          <div key={item.timesheet_mstr_key} className="selection-item">
            <span className="timesheet-id">{item.timesheet_mstr_key}</span>
            <span className="provider-name">{item.provider_name}</span>
            <span className="ts-date">{formatServiceDate(item.ts_date_local)}</span>
            <span className="budget">${Number(item.budget || 0).toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div className="selection-footer">
        <span className="footer-label">Total Amount</span>
        <span className="footer-value">${totalBudget.toFixed(2)}</span>
      </div>
      {visibleItems.length && (
        <div className="hidden-count">
          {visibleItems.length} pay request{visibleItems.length > 1 ? "s" : ""} selected
        </div>
      )}
    </div>
  );
};

const SELECTED_FIELD = "selected";
const DATA_ITEM_KEY = "timesheet_mstr_key";
const idGetter = getter(DATA_ITEM_KEY);

const PayRequestTracker = (pageProps) => {
  const cookiesData = getUserCredential();
  const dispatch = useDispatch();
  const history = useHistory();
  const hasWriteAccess = pageProps.writeAccess === 1;

  // Calculate initial dates
  const getInitialStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
  };

  const getInitialEndDate = () => {
    return new Date(); // Current date
  };

  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [holdEnabled, setHoldEnabled] = useState(undefined);
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [dateErrors, setDateErrors] = useState({ startDate: "", endDate: "" });
  const [hasSearched, setHasSearched] = useState(false);
  const [timeSheetData, setTimeSheetData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Raw data lists for filtering
  const [specialtyDataList, setSpecialtyDataList] = useState([]);
  const [payRequestTypeDataList, setPayRequestTypeDataList] = useState([]);
  const [providerDataList, setProviderDataList] = useState([]);

  const statusList = getCompAdminStatuses({ holdEnabled });

  // Field objects with state
  const [specialtyFields, setSpecialtyFields] = useState({
    specialtyData: [],
    specialtyDataValue: [{ id: "Select All", text: "Select All" }],
    filterValue: "",
    isSpecialtyDataLoading: false,
    search: "",
  });

  const [payRequestTypeFields, setPayRequestTypeFields] = useState({
    payRequestTypeData: [],
    payRequestTypeDataValue: [{ id: "Select All", text: "Select All" }],
    filterValue: "",
    isPayRequestTypeDataLoading: false,
    search: "",
  });

  const [providerFields, setProviderFields] = useState({
    providerData: [],
    providerDataValue: [{ id: "Select All", text: "Select All" }],
    filterValue: "",
    isProviderDataLoading: false,
    search: "",
  });

  const defaultPageSize = getTenantCookieData("record_per_page");
  const [pageData, setPageData] = useState({
    skip: 0,
    take: defaultPageSize.recordTake,
  });
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const [gridFilter, setGridFilter] = useState({ logic: "and", filters: [] });
  const [gridSort, setGridSort] = useState([]);
  const [resetGrid, setResetGrid] = useState(false);

  // Dynamic column state from API (mirrors specialty pattern)
  const [gridColumns, setGridColumns] = useState([]);
  const [originalColumns, setOriginalColumns] = useState([]);

  // Filter/sort state object (mirrors specialty pattern)
  const [filterData, setFilterData] = useState({
    isSort: false,
    isFilter: false,
    data: "",
    in_sort_number: DEFAULT_SORT.number,
    sort_order: DEFAULT_SORT.order,
  });

  const [selectedState, setSelectedState] = useState({});
  const [exportDataClick, setExportDataClick] = useState(false);
  const [exportData, setExportData] = useState([]);

  const [payRequestDetailedDialogVisible, setPayRequestDetailedDialogVisible] = useState(false);
  const [selectedTimesheetMstrKey, setSelectedTimesheetMstrKey] = useState(null);

  // State for checkbox selection and approval/rejection
  // Using Map to store summary data for display in confirmation popup
  const [selectedTimesheetKeys, setSelectedTimesheetKeys] = useState(new Map());
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingTxn, setIsGeneratingTxn] = useState(false);
  const [generateEventTxnEnabled, setGenerateEventTxnEnabled] = useState(false);
  const [autoGenerateEventTxn, setAutoGenerateEventTxn] = useState(false);
  const [eventTxnKeys, setEventTxnKeys] = useState([]);
  const [confirmationPopup, setConfirmationPopup] = useState({
    title: "",
    desc: "",
    isVisible: false,
    type: "",
    actionBtn: [],
    isCustomDesc: false,
    customComponent: null,
  });

  // Column reorder persistence (mirrors specialty)
  const { columnReorderData } = useColumnReorder(
    cookiesData,
    setGridColumns,
    "pay-request-tracker",
    "procare_system",
    originalColumns
  );

  const getStatusKeyFromTab = (tabIndex) => {
    if (tabIndex === 0) return null; // All
    return statusList[tabIndex - 1]?.timesheet_status_mstr_key || null;
  };

  const handleTabSelect = (tabIndex) => {
    setSelectedTab(tabIndex);
    setSelectedState({});
    setSelectedTimesheetKeys(new Map()); // Clear checkbox selections on tab change
    setHasSearched(false); // Hide grid until search is clicked
    setTimeSheetData([]); // Clear data
  };

  const handleStartDateChange = (event) => {
    const newStartDate = event.target.value;

    // Validate that start date is not after end date
    if (newStartDate && endDate && newStartDate > endDate) {
      setDateErrors((prev) => ({ ...prev, startDate: "Start date must be before end date" }));
      return;
    }

    setDateErrors((prev) => ({ ...prev, startDate: "" }));
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (event) => {
    const newEndDate = event.target.value;

    // Validate that end date is not before start date
    if (startDate && newEndDate && startDate > newEndDate) {
      setDateErrors((prev) => ({ ...prev, endDate: "End date must be after start date" }));
      return;
    }

    setDateErrors((prev) => ({ ...prev, endDate: "" }));
    setEndDate(newEndDate);
  };

  // Handler functions adapted from Provider Position pattern
  const filterChange = (event, setDataFields, moduleData, key) => {
    let dataValue = filterBy(moduleData.slice(), event.filter);
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
      filterValue: event.filter.value,
      search: event.filter,
    }));
  };

  const onOpenChange = (setDataFields, moduleData, key, dataFields, dataFieldsValue) => {
    let dataValue =
      dataFields.filterValue && dataFields.filterValue !== ""
        ? filterBy(dataFieldsValue.slice(), dataFields.search)
        : moduleData;
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
    }));
  };

  const handleChangeDropdownValue = (e, name, setDataFields, dataList, valueData, dataFields) => {
    if (valueData && valueData.filter((x) => x.text === "Select All").length > 0) {
      let index = e.value.findIndex((x) => x.text === "Select All");
      e.value.splice(index, 1);
    } else if (e.value && e.value.filter((x) => x.text === "Select All").length > 0 && e.value.length > 1) {
      e.value = [];
      e.value.push({ text: "Select All", id: "Select All" });
    }
    if (e.value.length === 0) {
      e.value.push({ text: "Select All", id: "Select All" });
    }

    let dataValue = [];
    if (e.value.length === 1 && e.value.some((x) => x.text === "Select All")) {
      dataValue.push({ text: "Select All", id: "Select All" });
    } else {
      dataValue = [...e.value];
    }
    setDataFields({
      ...dataFields,
      [name]: dataValue,
    });
  };

  const handleBlurDropdown = (setDataFields) => {
    setDataFields((prev) => ({
      ...prev,
      filterValue: "",
      search: "",
    }));
  };

  // Pagination handler - now calls API with new pagination params
  const handlePageChange = async (event) => {
    // Check if this is a per-page dropdown change by verifying the value has recordTake property
    if (event.syntheticEvent?.target?.value?.recordTake !== undefined) {
      // Per-page dropdown was changed
      const newPageSize = event.syntheticEvent.target.value;
      setPageSize(newPageSize);
      setPageData({
        skip: 0,
        take: newPageSize.recordTake,
      });

      // Fetch first page with new page size (use current filter/sort state)
      await fetchTimesheetData(0, newPageSize.recordTake);
    } else {
      // Regular pagination navigation
      const newPageData = event.page;
      setPageData({
        skip: newPageData.skip,
        take: newPageData.take,
      });

      // Fetch data for the new page (use current filter/sort state)
      await fetchTimesheetData(newPageData.skip, newPageData.take);
    }
  };

  // Selection change handler (like specialtyGrid)
  const onSelectionChange = (event) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedState(newSelectedState);

    const keys = Object.keys(newSelectedState).filter((key) => newSelectedState[key] === true);
    if (keys.length > 0) {
      const selectedItem = timeSheetData.find((item) => item.timesheet_mstr_key.toString() === keys[0]);
    } else {
    }
  };

  // Row click handler for selection
  const handleRowClick = (event) => {
    const dataItem = event.dataItem;
    const id = idGetter(dataItem);

    // Toggle selection - if already selected, deselect; otherwise select
    const isCurrentlySelected = selectedState[id];

    if (isCurrentlySelected) {
      // Deselect the row
      setSelectedState({});
    } else {
      // Select only this row (single selection mode)
      setSelectedState({ [id]: true });
    }
  };

  // Handle column menu (filter/sort) - mirrors specialty pattern
  const handleColumnMenu = async (columnMenuData, isFilter, isSort, filterObj) => {
    let index = 1;
    let filterSkip = 0;
    let filterTake = pageData.take;

    if (isFilter) {
      filterSkip = 0;
      filterTake = pageSize.recordTake;
      setGridFilter(filterObj);
      setPageData({ skip: 0, take: filterTake });
    }
    if (isSort) {
      setGridSort(columnMenuData);
    }

    // Determine positional sort index from gridColumns
    if (gridColumns.length > 0 && columnMenuData[0] !== undefined) {
      index = gridColumns.findIndex((col) => col.field === columnMenuData[0].field) + 1;
      // Ensure index is within valid range (1 to column count)
      if (index <= 0) index = 1;
    }

    const filterString = isFilter ? getfilterDataString(columnMenuData, gridColumns) : filterData.data;

    let computedIsFilter = false;
    if (filterString !== "") {
      computedIsFilter = true;
    }

    let sortDir = filterData.isSort && columnMenuData.length ? filterData.sort_order : DEFAULT_SORT.order;
    let sortNumber = filterData.isSort && columnMenuData.length ? filterData.in_sort_number : DEFAULT_SORT.number;
    let computedIsSort = !columnMenuData.length ? false : filterData.isSort;

    if (columnMenuData[0] !== undefined && columnMenuData[0].dir !== undefined) {
      computedIsSort = true;
      sortDir = columnMenuData[0].dir.toUpperCase();
      sortNumber = index;
    }

    setFilterData({
      ...filterData,
      isFilter: computedIsFilter,
      isSort: computedIsSort,
      data: filterString,
      in_sort_number: sortNumber,
      sort_order: sortDir,
    });

    // Fetch with new filter/sort
    await fetchTimesheetData(filterSkip, filterTake, computedIsFilter ? filterString : null, sortNumber, sortDir);
  };

  // Clear filter handler - resets backend filter and refetches
  const onClearFilter = async () => {
    setGridFilter({ logic: "and", filters: [] });
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });
    setPageData({ skip: 0, take: pageData.take });
    await fetchTimesheetData(0, pageData.take, null, DEFAULT_SORT.number, DEFAULT_SORT.order);
  };

  // Refresh handler - resets all filter/sort and refetches
  const onRefreshClick = async () => {
    setGridFilter({ logic: "and", filters: [] });
    setGridSort([]);
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });
    setPageData({ skip: 0, take: pageSize.recordTake });
    setSelectedState({});
    setSelectedTimesheetKeys(new Map()); // Clear checkbox selections on refresh
    setResetGrid(true);

    // Refetch current page
    if (hasSearched) {
      await fetchTimesheetData(0, pageSize.recordTake, null, DEFAULT_SORT.number, DEFAULT_SORT.order);
    }
  };

  // Action click handler
  const onActionClick = (dataItem) => {
    setSelectedTimesheetMstrKey(dataItem.timesheet_mstr_key);
    setPayRequestDetailedDialogVisible(true);
  };

  const handleRowDoubleClick = (event) => {
    const dataItem = event.dataItem;
    const id = idGetter(dataItem);
    setSelectedTimesheetMstrKey(id);
    setPayRequestDetailedDialogVisible(true);
  };

  // Check if any rows are selected for enabling approve/reject buttons
  // Check if any rows are selected (from the persistent Set)
  const hasSelectedRows = useMemo(() => selectedTimesheetKeys.size > 0, [selectedTimesheetKeys]);

  // Header checkbox handler - select/deselect all eligible items on current page
  const headerCheckboxOnChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      setIsHeaderChecked(checked);
      const selectableItems = timeSheetData.filter((item) => item.status?.timesheet_status_mstr_key === 5);

      setSelectedTimesheetKeys((prev) => {
        const newMap = new Map(prev);
        selectableItems.forEach((item) => {
          if (checked) {
            newMap.set(item.timesheet_mstr_key, {
              timesheet_mstr_key: item.timesheet_mstr_key,
              provider_name: item.provider_name,
              ts_date_local: item.ts_date_local,
              budget: item.budget,
            });
          } else {
            newMap.delete(item.timesheet_mstr_key);
          }
        });
        return newMap;
      });
    },
    [timeSheetData]
  );

  // Cell checkbox handler - adds/removes entries from the persistent Map
  const cellCheckboxOnChange = useCallback((e, props) => {
    const { timesheet_mstr_key, provider_name, ts_date_local_raw, budget } = props.dataItem;
    setSelectedTimesheetKeys((prev) => {
      const newMap = new Map(prev);
      if (e.target.value) {
        newMap.set(timesheet_mstr_key, { timesheet_mstr_key, provider_name, ts_date_local: ts_date_local_raw, budget });
      } else {
        newMap.delete(timesheet_mstr_key);
      }
      return newMap;
    });
  }, []);

  // Set global reference for checkbox change handler and write access
  globalCellCheckboxOnChange = cellCheckboxOnChange;
  globalHasWriteAccess = hasWriteAccess;

  // Handle approve button click - show confirmation dialog
  const handleApproveClick = () => {
    setConfirmationPopup({
      title: "Confirm Approval",
      desc: "",
      isVisible: true,
      type: "APPROVE_CONFIRM",
      actionBtn: getApproveButtonActions(false),
      isCustomDesc: true,
      customComponent: (
        <div className="confirmation-content">
          <p>Are you sure you want to approve the following pay request(s)?</p>
          <SelectedPayRequestsList selectedItems={selectedTimesheetKeys} />
        </div>
      ),
    });
  };

  // Handle reject button click - show confirmation dialog
  const handleRejectClick = () => {
    setConfirmationPopup({
      title: "Confirm Rejection",
      desc: "",
      isVisible: true,
      type: "REJECT_CONFIRM",
      actionBtn: getRejectButtonActions(false),
      isCustomDesc: true,
      customComponent: (
        <div className="confirmation-content">
          <p>Are you sure you want to reject the following pay request(s)?</p>
          <SelectedPayRequestsList selectedItems={selectedTimesheetKeys} maxVisible={10} />
        </div>
      ),
    });
  };

  // Execute approval
  const handleApproveAction = async () => {
    await submitApproval("4"); // Status 4 = Approved
  };

  // Execute rejection
  const handleRejectAction = async () => {
    await submitApproval("3"); // Status 3 = Rejected
  };

  // Submit approval/rejection to API
  const submitApproval = async (status) => {
    setIsSubmitting(true);
    handleCancelAction();
    try {
      // Use keys from the persistent Map
      const selectedTimesheets = Array.from(selectedTimesheetKeys.keys()).map((key) => ({ id: key }));

      const response = await insertTimesheetApproval(cookiesData, dispatch, selectedTimesheets, status);

      // Get successful and failed keys from response
      const successKeys = response?.data?.successTimesheetMstrKey || [];
      const failedKeys = response?.data?.failedTimesheetMstrKey || [];

      if (successKeys.length > 0) {
        // Remove all timesheets from this batch from selection (success + failed)
        setSelectedTimesheetKeys((prev) => {
          const newMap = new Map(prev);
          const keysToClear = new Set([...successKeys, ...failedKeys]);
          keysToClear.forEach((key) => newMap.delete(key));
          return newMap;
        });

        // Show appropriate message
        if (failedKeys.length > 0) {
          dispatch(
            setNewMessage({
              message: `${successKeys.length} timesheet(s) ${status === "4" ? "approved" : "rejected"} successfully. ${failedKeys.length} failed.`,
              type: "Warning",
              show: true,
            })
          );
        } else {
          dispatch(
            setNewMessage({
              message: status === "4" ? "Timesheet(s) approved successfully" : "Timesheet(s) rejected successfully",
              type: "Success",
              show: true,
            })
          );
        }
      } else if (failedKeys.length > 0) {
        // All failed — clear failed keys from selection; show warning
        setSelectedTimesheetKeys((prev) => {
          const newMap = new Map(prev);
          failedKeys.forEach((key) => newMap.delete(key));
          return newMap;
        });
        dispatch(
          setNewMessage({
            message:
              response?.data?.out_parameters?.out_error_msg ||
              `${failedKeys.length} timesheet(s) failed to ${status === "4" ? "approve" : "reject"}.`,
            type: "Warning",
            show: true,
          })
        );
      } else if (response?.data?.out_parameters?.out_ret_code === 0) {
        // Fallback for responses without success/failed arrays
        dispatch(
          setNewMessage({
            message: status === "4" ? "Timesheet(s) approved successfully" : "Timesheet(s) rejected successfully",
            type: "Success",
            show: true,
          })
        );
        setSelectedTimesheetKeys(new Map());
      } else {
        // Generic error
        dispatch(
          setNewMessage({
            message: response?.data?.out_parameters?.out_error_msg || "An error occurred",
            type: "Error",
            show: true,
          })
        );
      }

      // Refresh grid data
      await fetchTimesheetData(pageData.skip, pageData.take);
    } catch (error) {
      console.error("Error submitting approval:", error);
      dispatch(
        setNewMessage({
          message: "An error occurred while processing your request",
          type: "Error",
          show: true,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel/close confirmation dialog
  const handleCancelAction = () => {
    setConfirmationPopup({
      ...confirmationPopup,
      isVisible: false,
    });
  };

  // Local button action functions for approve/reject dialogs
  const getApproveButtonActions = (submitting) => [
    {
      name: "Cancel",
      onClick: handleCancelAction,
      disabled: submitting,
    },
    {
      name: submitting ? "Approving..." : "Approve",
      disabled: submitting,
      onClick: handleApproveAction,
    },
  ];

  const getRejectButtonActions = (submitting) => [
    {
      name: "Cancel",
      onClick: handleCancelAction,
      disabled: submitting,
    },
    {
      name: submitting ? "Rejecting..." : "Reject",
      disabled: submitting,
      onClick: handleRejectAction,
    },
  ];

  const getGenerateEventTxnButtonActions = (submitting, keys) => [
    {
      name: "Cancel",
      onClick: handleCancelAction,
      disabled: submitting,
    },
    {
      name: submitting ? "Generating..." : "Generate",
      disabled: submitting || !(keys && keys.length),
      onClick: () => handleGenerateEventTxnAction(keys),
    },
  ];

  // Gathers all Ready-for-Payroll timesheet keys (same row set as Export to Excel), asks the
  // backend for the eligible count via /transactions/eligible-count, then opens the
  // confirmation modal showing that count.
  const handleGenerateEventTransactionsClick = async () => {
    setIsGeneratingTxn(true);
    try {
      const { filteredSpecialties, filteredPayRequestTypes, filteredProviders } = buildSelectedFilterArrays();
      const exportRes = await getExportAdminTimesheets(
        cookiesData,
        dispatch,
        startDate,
        endDate,
        getStatusKeyFromTab(selectedTab),
        filteredSpecialties,
        filteredPayRequestTypes,
        filteredProviders,
        getInFilter(),
        getInSortNumber(),
        getInSortOrder()
      );
      const keys = (exportRes.data || []).map((r) => ({ timesheet_mstr_key: r.timesheet_mstr_key }));
      if (keys.length === 0) {
        dispatch(setNewMessage({ message: "No pay requests available to process.", type: "Warning", show: true }));
        return;
      }
      const countRes = await getEligibleTransactionCount(cookiesData, dispatch, keys);
      if (!countRes.status) {
        dispatch(
          setNewMessage({
            message: countRes.error || "Unable to determine eligible count.",
            type: "Error",
            show: true,
          })
        );
        return;
      }
      const eligibleKeys = (countRes.items || []).map((r) => ({ timesheet_mstr_key: r.timesheet_mstr_key }));
      const eligibleMap = new Map(
        (countRes.items || []).map((r) => [
          r.timesheet_mstr_key,
          {
            timesheet_mstr_key: r.timesheet_mstr_key,
            provider_name: r.provider_name,
            ts_date_local: r.ts_date_local,
            budget: r.budget,
          },
        ])
      );
      setEventTxnKeys(eligibleKeys);
      const isEmptyEligible = eligibleKeys.length === 0;
      setConfirmationPopup({
        title: "Generate Event Transactions",
        desc: "",
        isVisible: true,
        type: "GENERATE_EVENT_TXN",
        actionBtn: getGenerateEventTxnButtonActions(false, eligibleKeys),
        isCustomDesc: true,
        customComponent: isEmptyEligible ? (
          <div className="confirmation-content">
            <p>No pay requests eligible to process.</p>
          </div>
        ) : (
          <div className="confirmation-content">
            <p>Are you sure you want to generate event transactions for the following pay request(s)?</p>
            <p className="generate-event-txn-warning">
              <strong>Warning:</strong> Before sending payments to Events, please ensure that all intended transactions are included and approved.
              <br />
              Omitting any transactions may require them to be recalculated and reprocessed, which could change the final payment amounts.
            </p>
            <SelectedPayRequestsList selectedItems={eligibleMap} />
          </div>
        ),
      });
    } catch (error) {
      console.error("Error preparing event transactions:", error);
      dispatch(setNewMessage({ message: "Failed to prepare event transactions.", type: "Error", show: true }));
    } finally {
      setIsGeneratingTxn(false);
    }
  };

  // Confirms generation: POSTs eligible keys from /eligible-count items.
  // The backend re-checks eligibility on /transactions/process, so a stale key is harmless.
  const handleGenerateEventTxnAction = async (keys = eventTxnKeys) => {
    setIsSubmitting(true);
    handleCancelAction();
    try {
      const res = await processEventTransactions(cookiesData, dispatch, keys);
      if (res.status && res.success) {
        dispatch(
          setNewMessage({
            message: `Event transaction generation started for ${res.count} pay request(s).`,
            type: "Success",
            show: true,
          })
        );
        await fetchTimesheetData(pageData.skip, pageData.take);
      } else {
        dispatch(
          setNewMessage({
            message: res.error || "Failed to generate event transactions.",
            type: "Error",
            show: true,
          })
        );
      }
    } catch (error) {
      console.error("Error generating event transactions:", error);
      dispatch(setNewMessage({ message: "Failed to generate event transactions.", type: "Error", show: true }));
    } finally {
      setIsSubmitting(false);
      setEventTxnKeys([]);
    }
  };

  // Helper function to get current filter params
  const getInFilter = () => {
    return filterData.isFilter ? filterData.data : null;
  };
  const getInSortNumber = () => {
    return filterData.isSort ? filterData.in_sort_number : DEFAULT_SORT.number;
  };
  const getInSortOrder = () => {
    return filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order;
  };

  // Builds the { filteredSpecialties, filteredPayRequestTypes, filteredProviders } arrays from
  // current dropdown selections ("Select All" maps to an empty array). Shared by fetchTimesheetData,
  // getExportData, and Generate Event Transactions so all three stay in sync.
  const buildSelectedFilterArrays = () => {
    const filteredSpecialties =
      specialtyFields.specialtyDataValue.filter((x) => x.text !== "Select All").length === 0
        ? []
        : specialtyFields.specialtyDataValue.map((item) => ({
            specialty_mstr_key: item.id,
            specialty_name: item.text,
          }));

    const filteredPayRequestTypes =
      payRequestTypeFields.payRequestTypeDataValue.filter((x) => x.text !== "Select All").length === 0
        ? []
        : payRequestTypeFields.payRequestTypeDataValue.map((item) => ({
            contract_type_mstr_key: item.id,
            contract_type: item.text,
          }));

    const filteredProviders =
      providerFields.providerDataValue.filter((x) => x.text !== "Select All").length === 0
        ? []
        : providerFields.providerDataValue.map((item) => ({
            provider_mstr_key: item.id,
            provider_name: item.text,
          }));

    return { filteredSpecialties, filteredPayRequestTypes, filteredProviders };
  };

  // Helper function to fetch timesheet data with pagination and backend filter/sort
  const fetchTimesheetData = async (skip, take, overrideFilter, overrideSortNumber, overrideSortOrder) => {
    setIsLoading(true);
    try {
      const { filteredSpecialties, filteredPayRequestTypes, filteredProviders } = buildSelectedFilterArrays();

      // Use override params if provided, otherwise use current filterData state
      const inFilter = overrideFilter !== undefined ? overrideFilter : getInFilter();
      const inSortNumber = overrideSortNumber !== undefined ? overrideSortNumber : getInSortNumber();
      const inSortOrder = overrideSortOrder !== undefined ? overrideSortOrder : getInSortOrder();

      const response = await getAdminTimesheets(
        cookiesData,
        dispatch,
        startDate,
        endDate,
        selectedTab === 0 ? null : getStatusKeyFromTab(selectedTab), // Pass null for "All" tab
        filteredSpecialties,
        filteredPayRequestTypes,
        filteredProviders,
        skip,
        take,
        inFilter,
        inSortNumber,
        inSortOrder
      );

      // Initialize dynamic columns from API response (only on first load or when columns not yet set)
      if (response.columData && response.columData.length > 0 && originalColumns.length === 0) {
        const columData = response.columData;
        // Apply column overrides (similar to specialty pattern)
        columData.forEach((cItem) => {
          cItem.minWidth = 150;
        });
        setGridColumns(columData);
        setOriginalColumns(columData);
      }

      // Add is_selected property for checkbox tracking
      const dataWithSelection = (response.data || []).map((item, idx) => ({
        ...item,
        is_selected: 0,
        rowId: idx,
      }));
      setTimeSheetData(dataWithSelection);
      setTotalCount(response.totalCount || 0);
      setIsHeaderChecked(false); // Reset header checkbox when data is refreshed
    } catch (error) {
      console.error("Error fetching timesheet data:", error);
      setTimeSheetData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Search button click handler
  const handleSearchClick = async () => {
    // Reset grid filters, sorting, and backend filter/sort state
    setGridFilter({ logic: "and", filters: [] });
    setGridSort([]);
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });
    setResetGrid(true);
    setPageData({ skip: 0, take: pageSize.recordTake });
    setHasSearched(true);

    // Fetch first page
    await fetchTimesheetData(0, pageSize.recordTake, null, DEFAULT_SORT.number, DEFAULT_SORT.order);
  };

  // Export to Excel function - passes current filter/sort to backend
  const getExportData = async () => {
    setExportData([]);
    try {
      const { filteredSpecialties, filteredPayRequestTypes, filteredProviders } = buildSelectedFilterArrays();

      const response = await getExportAdminTimesheets(
        cookiesData,
        dispatch,
        startDate,
        endDate,
        selectedTab === 0 ? null : getStatusKeyFromTab(selectedTab),
        filteredSpecialties,
        filteredPayRequestTypes,
        filteredProviders,
        getInFilter(),
        getInSortNumber(),
        getInSortOrder()
      );

      if (response.data && response.data.length > 0) {
        response.data.forEach((rItem) => {
          if (rItem.ts_date_local && rItem.ts_date_local !== null) {
            rItem.ts_date_local = formatServiceDate(rItem.ts_date_local);
          }
          if (rItem.post_date_local && rItem.post_date_local !== null) {
            rItem.post_date_local = formatServiceDate(rItem.post_date_local);
          }
          if (rItem.status_changed_date && rItem.status_changed_date !== null) {
            rItem.status_changed_date =
              convertTimeZone({
                date: rItem.status_changed_date,
                convertTimeZone: getUserTimezone(),
                outputFormat: "MM/dd/yyyy",
              }) || null;
          }
        });

        // Process export data similar to provider position
        if (response.columData && response.columData.length > 0) {
          response.columData.forEach((item) => {
            if (item.type === "number") {
              // Skip budget and rate fields - they will be formatted separately with exact decimals
              if (item.field === "budget" || item.field === "rate") {
                return;
              }
              response.data.forEach((rItem) => {
                if (rItem[item.field] === "" || rItem[item.field] === "0.00000000" || rItem[item.field] === null) {
                  rItem[item.field] = null;
                } else {
                  let value = rItem[item.field].toString();
                  if (value.includes(".")) {
                    value = value.replace(/\.?0+$/, "");
                  }
                  rItem[item.field] = value === "" ? "0" : value;
                }
              });
            }

            if (item.type === "boolean") {
              response.data.forEach((rItem) => {
                rItem[item.field] = Number(rItem[item.field]) ? "true" : "false";
              });
            }
          });
        }

        // Format budget and rate with exact decimal precision
        response.data.forEach((rItem) => {
          if (rItem.budget !== null && rItem.budget !== undefined && rItem.budget !== "") {
            rItem.budget = Number(rItem.budget).toFixed(2);
          }
          if (rItem.rate !== null && rItem.rate !== undefined && rItem.rate !== "") {
            rItem.rate = Number(rItem.rate).toFixed(2);
          }
          if (
            rItem.status?.timesheet_status_mstr_key !== null &&
            rItem.status?.timesheet_status_mstr_key !== undefined &&
            rItem.status?.timesheet_status_mstr_key !== ""
          ) {
            rItem.status = rItem.status?.display_name;
          }
        });

        // Calculate total_quantity, add timesheet_name, format approval_level_sortorder
        response.data.forEach((rItem) => {
          rItem.total_quantity = formatTimesheetQuantity(rItem);
          rItem.timesheet_name = rItem.timesheet_mstr_key.toString();

          if (
            rItem.approval_level_sortorder !== null &&
            rItem.approval_level_sortorder !== undefined &&
            rItem.approval_level_sortorder !== ""
          ) {
            rItem.approval_level_sortorder = `Level ${rItem.approval_level_sortorder}`;
          } else {
            rItem.approval_level_sortorder = "N/A";
          }
        });

        setExportData(response.data);
        setExportDataClick(false);
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setExportDataClick(false);
    }
  };

  useEffect(() => {
    const loadHoldPref = async () => {
      const prefs = await GetSystemConfigDataByCode(
        {
          in_tenant_id: cookiesData.out_tenant_id,
          pref_code: [
            "sys_hold_status_notif",
            "is_mobile_enabled",
            "sys_gen_pr_evnt_txn_notif",
            "sys_auto_pr_evnt_txn",
          ],
        },
        history,
        dispatch
      );
      setHoldEnabled(prefs?.sys_hold_status_notif === "1");
      setGenerateEventTxnEnabled(
        prefs?.sys_gen_pr_evnt_txn_notif === "1" && prefs?.is_mobile_enabled === "1"
      );
      setAutoGenerateEventTxn(prefs?.sys_auto_pr_evnt_txn === "1");
    };
    loadHoldPref();
  }, [cookiesData.out_tenant_id, dispatch, history]);

  // Tab 0 is All and statusList renders at index + 1, so dropping hold
  // shifts every later tab down one.
  useEffect(() => {
    if (holdEnabled !== false) return;
    const holdTab =
      getCompAdminStatuses().findIndex((s) => s.value === "hold") + 1;
    setSelectedTab((tab) => (tab === holdTab ? 0 : tab > holdTab ? tab - 1 : tab));
  }, [holdEnabled]);

  useEffect(() => {
    const fetchSpecialtyData = async () => {
      try {
        setSpecialtyFields((prev) => ({ ...prev, isSpecialtyDataLoading: true }));
        const specialtyResponse = await getAllSpecialty(cookiesData, dispatch);
        if (specialtyResponse && specialtyResponse.data && specialtyResponse.data.row_result) {
          const data = specialtyResponse.data.row_result;
          // Transform data to { id, text } format
          const transformedData = data.map((item) => ({
            id: item.specialty_mstr_key,
            text: item.specialty_name,
          }));
          setSpecialtyDataList(transformedData);
          setSpecialtyFields((prev) => ({
            ...prev,
            specialtyData: transformedData,
            specialtyDataValue: transformedData.length > 0 ? [{ id: "Select All", text: "Select All" }] : [],
            isSpecialtyDataLoading: false,
          }));
        } else {
          setSpecialtyDataList([]);
          setSpecialtyFields((prev) => ({
            ...prev,
            specialtyData: [],
            specialtyDataValue: [],
            isSpecialtyDataLoading: false,
          }));
        }
      } catch (error) {
        console.error("Error fetching specialty data:", error);
        setSpecialtyDataList([]);
        setSpecialtyFields((prev) => ({
          ...prev,
          specialtyData: [],
          specialtyDataValue: [],
          isSpecialtyDataLoading: false,
        }));
      }
    };

    const fetchContractTypeAndProviderData = async () => {
      try {
        setPayRequestTypeFields((prev) => ({ ...prev, isPayRequestTypeDataLoading: true }));
        setProviderFields((prev) => ({ ...prev, isProviderDataLoading: true }));

        // Fetch pay request type data from getAllPayRequestType
        const payRequestTypeResponse = await getAllPayRequestType(cookiesData, dispatch);
        const providerResponse = await getAllProviders(cookiesData, dispatch);
        if (payRequestTypeResponse && payRequestTypeResponse.data && payRequestTypeResponse.data.row_result) {
          const payRequestTypes = payRequestTypeResponse.data.row_result;
          // Transform pay request type data
          const transformedPayRequestTypes = (payRequestTypes || []).map((item) => ({
            id: item.task_category_mstr_key,
            text: item.name,
          }));
          setPayRequestTypeDataList(transformedPayRequestTypes);
          setPayRequestTypeFields((prev) => ({
            ...prev,
            payRequestTypeData: transformedPayRequestTypes,
            payRequestTypeDataValue:
              transformedPayRequestTypes.length > 0 ? [{ id: "Select All", text: "Select All" }] : [],
            isPayRequestTypeDataLoading: false,
          }));
        } else {
          setPayRequestTypeDataList([]);
          setPayRequestTypeFields((prev) => ({
            ...prev,
            payRequestTypeData: [],
            payRequestTypeDataValue: [],
            isPayRequestTypeDataLoading: false,
          }));
        }

        // Fetch provider data from getAllProviders
        if (providerResponse && providerResponse.data && providerResponse.data.row_result) {
          const providers = providerResponse.data.row_result;

          // Transform provider data
          const transformedProviders = (providers || []).map((item) => ({
            id: item.provider_mstr_key,
            text: item.provider_name,
          }));
          setProviderDataList(transformedProviders);
          setProviderFields((prev) => ({
            ...prev,
            providerData: transformedProviders,
            providerDataValue: transformedProviders.length > 0 ? [{ id: "Select All", text: "Select All" }] : [],
            isProviderDataLoading: false,
          }));
        } else {
          setProviderDataList([]);
          setProviderFields((prev) => ({
            ...prev,
            providerData: [],
            providerDataValue: [],
            isProviderDataLoading: false,
          }));
        }
      } catch (error) {
        console.error("Error fetching contract type and provider data:", error);
        setProviderDataList([]);
        setProviderFields((prev) => ({
          ...prev,
          providerData: [],
          providerDataValue: [],
          isProviderDataLoading: false,
        }));
        setPayRequestTypeDataList([]);
        setPayRequestTypeFields((prev) => ({
          ...prev,
          payRequestTypeData: [],
          payRequestTypeDataValue: [],
          isPayRequestTypeDataLoading: false,
        }));
      }
    };

    fetchSpecialtyData();
    fetchContractTypeAndProviderData();
  }, []);

  // Display-only transforms (dates to Date objects, approval level formatting)
  // Filtering and sorting are now handled by the backend
  // is_selected is derived from the persistent selectedTimesheetKeys Map
  const displayData = useMemo(() => {
    return timeSheetData.map((item) => ({
      ...item,
      is_selected: selectedTimesheetKeys.has(item.timesheet_mstr_key) ? 1 : 0,
      ts_date_local_raw: item.ts_date_local,
      post_date_local_raw: item.post_date_local,
      status_changed_date_raw: item.status_changed_date,
      ts_date_local: parseAndNormalizeDate(item.ts_date_local),
      post_date_local: parseAndNormalizeDate(item.post_date_local),
      status_changed_date: parseAndNormalizeDate(item.status_changed_date),
      approval_level_sortorder:
        item.approval_level_sortorder !== null &&
        item.approval_level_sortorder !== undefined &&
        item.approval_level_sortorder !== ""
          ? `Level ${item.approval_level_sortorder}`
          : "N/A",
    }));
  }, [timeSheetData, selectedTimesheetKeys]);

  // Sync header checkbox state based on current page selections
  useEffect(() => {
    const selectableItems = timeSheetData.filter((item) => item.status?.timesheet_status_mstr_key === 5);
    const allSelected =
      selectableItems.length > 0 && selectableItems.every((item) => selectedTimesheetKeys.has(item.timesheet_mstr_key));
    setIsHeaderChecked(allSelected);
  }, [timeSheetData, selectedTimesheetKeys]);

  // Update dialog buttons when isSubmitting changes
  useEffect(() => {
    if (confirmationPopup.isVisible) {
      setConfirmationPopup((prev) => ({
        ...prev,
        actionBtn:
          prev.type === "APPROVE_CONFIRM"
            ? getApproveButtonActions(isSubmitting)
            : prev.type === "GENERATE_EVENT_TXN"
            ? getGenerateEventTxnButtonActions(isSubmitting, eventTxnKeys)
            : getRejectButtonActions(isSubmitting),
      }));
    }
  }, [isSubmitting]);

  // Reset to page 1, clear filters/sort when tab changes and refetch data
  useEffect(() => {
    if (hasSearched) {
      setGridFilter({ logic: "and", filters: [] });
      setGridSort([]);
      setFilterData({
        isSort: false,
        isFilter: false,
        data: "",
        in_sort_number: DEFAULT_SORT.number,
        sort_order: DEFAULT_SORT.order,
      });
      setResetGrid(true);
      const currentTake = pageData.take;
      setPageData({ skip: 0, take: currentTake });
      fetchTimesheetData(0, currentTake, null, DEFAULT_SORT.number, DEFAULT_SORT.order);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  // Reset grid filters and filter state when dates change
  useEffect(() => {
    setGridFilter({ logic: "and", filters: [] });
    setGridSort([]);
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });
    setResetGrid(true);
  }, [startDate, endDate]);

  // Reset the resetGrid flag after it's been processed by DataTable
  useEffect(() => {
    if (resetGrid) {
      const timer = setTimeout(() => {
        setResetGrid(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [resetGrid]);

  // Calculate grid height based on filter selections or error messages
  const calculateGridHeight = () => {
    const maxFilterLength = Math.max(
      specialtyFields.specialtyDataValue.length,
      payRequestTypeFields.payRequestTypeDataValue.length,
      providerFields.providerDataValue.length
    );

    const isDateError = dateErrors.startDate || dateErrors.endDate;

    if (maxFilterLength >= 3) {
      return "52vh";
    } else if (maxFilterLength >= 2 || isDateError) {
      return "54vh";
    }
    return "57vh";
  };

  const gridHeight = calculateGridHeight();

  // Check if there are any selectable items (status 5)
  const hasSelectableItems = useMemo(
    () => timeSheetData.some((item) => item.status?.timesheet_status_mstr_key === 5),
    [timeSheetData]
  );

  // Map of custom cell renderers by field name
  const getCellRenderer = (field) => {
    switch (field) {
      case "is_selected":
        return CheckboxCell;
      case "status_display_name":
        return StatusCell;
      case "approval_level_sortorder":
        return (props) => {
          const formatted = props.dataItem.approval_level_sortorder || "N/A";
          return (
            <td className="k-table-td">
              <span title={formatted}>{formatted}</span>
            </td>
          );
        };
      case "ts_date_local":
      case "post_date_local":
        return (props) => {
          const rawDate = props.dataItem[`${props.field}_raw`];
          const formattedDate = rawDate ? formatServiceDate(rawDate) : "";
          return (
            <td className="k-table-td">
              <span title={formattedDate}>{formattedDate}</span>
            </td>
          );
        };
      case "budget":
      case "rate":
        return (props) => {
          const val = props.dataItem[props.field];
          const formatted = val !== null && val !== undefined ? Number(val).toFixed(2) : "0.00";
          return (
            <td className="k-table-td">
              <span title={formatted}>{formatted}</span>
            </td>
          );
        };
      case "period_type":
        return (props) => {
          const periodType = props.dataItem.period_type || "";
          const formatted = periodType.charAt(0).toUpperCase() + periodType.slice(1);
          return (
            <td className="k-table-td">
              <span title={formatted}>{formatted}</span>
            </td>
          );
        };
      case "total_quantity":
        return (props) => {
          const formatted = formatTimesheetQuantity(props.dataItem);
          return (
            <td className="k-table-td">
              <span title={formatted}>{formatted}</span>
            </td>
          );
        };
      default:
        return DefaultCell;
    }
  };

  // Generate dynamic customColumn from gridColumns
  const customColumn = useMemo(() => {
    // All columns from API metadata
    return gridColumns
      .filter((col) => col.show && col.title)
      .map((column, idx) => {
        const cellRenderer = getCellRenderer(column.field);
        const isCheckboxColumn = column.field === "is_selected";

        // Special handling for is_selected checkbox column
        if (isCheckboxColumn) {
          const checkboxColumnWithProps = {
            ...column,
            aditionalParam: {
              assignCheckboxProps: {
                checked: isHeaderChecked,
                onChange: headerCheckboxOnChange,
                disabled: !hasWriteAccess || !hasSelectableItems || timeSheetData.length === 0,
              },
              dataTableProps: {
                id: "is_selected",
              },
            },
          };

          return (
            <Column
              {...columnMenuProps(checkboxColumnWithProps, "pay-request-tracker")}
              key={column.field}
              field={column.field}
              title={column.title}
              width="150px"
              sortable={false}
              cell={cellRenderer}
            />
          );
        }

        // Regular data columns
        const isTotalQuantityColumn = column.field === "total_quantity";
        return (
          <Column
            {...columnMenuProps(column, "pay-request-tracker")}
            key={column.field || idx}
            field={column.field}
            title={column.title}
            width={column.min_width > 0 ? `${column.min_width}px` : "150px"}
            sortable={!isTotalQuantityColumn}
            cell={cellRenderer}
          />
        );
      });
  }, [gridColumns, isHeaderChecked, headerCheckboxOnChange, hasSelectableItems, timeSheetData.length, hasWriteAccess]);

  // Handle menu item clicks
  const handleMenuItemClick = (module) => {
    switch (module) {
      case "export":
        // Prevent export if no records
        if (!hasSearched || timeSheetData.length === 0) {
          return;
        }
        setExportDataClick(true);
        break;
      case "generateEventTransactions":
        if (!hasSearched || timeSheetData.length === 0) {
          return;
        }
        handleGenerateEventTransactionsClick();
        break;
      default:
        break;
    }
  };

  // Only show on the Ready-for-Payroll tab (status key 6) when Generate + Mobile are On. An absent
  // action_menu key isn't rendered (see headerPage.jsx), so we gate here instead of via `disabled`.
  const showGenerateEventTxn =
    generateEventTxnEnabled &&
    !autoGenerateEventTxn &&
    getStatusKeyFromTab(selectedTab) === 6;

  const headerConfig = {
    title: "Pay Request Tracker",
    id: "pay-request-tracker",
    action_menu: {
      export: {
        fx: handleMenuItemClick,
        disabled: !hasSearched || timeSheetData.length === 0,
      },
      ...(showGenerateEventTxn
        ? {
            generateEventTransactions: {
              fx: handleMenuItemClick,
              disabled: !hasSearched || timeSheetData.length === 0,
            },
          }
        : {}),
    },
  };

  return (
    <div className="Main pay-request-tracker">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
        </div>
        <div className="content-body">
          <div className="pay-request-tracker-content">
            <div className="pay-request-tracker-tabs">
              <div className="custom-tabs-container">
                <div className="custom-tabs">
                  <div className={`custom-tab ${selectedTab === 0 ? "active" : ""}`} onClick={() => handleTabSelect(0)}>
                    All
                  </div>
                  {statusList.map((status, index) => (
                    <div
                      key={status.timesheet_status_mstr_key}
                      className={`custom-tab ${selectedTab === index + 1 ? "active" : ""}`}
                      onClick={() => handleTabSelect(index + 1)}
                    >
                      {status.display_name}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pay-request-tracker-controls">
              <div className="filter-row">
                <div className="filter-item">
                  <label className="filter-label">Start Date</label>
                  <DatePicker
                    value={startDate}
                    onChange={handleStartDateChange}
                    placeholder="Start Date"
                    className="filter-datepicker"
                  />
                  {dateErrors.startDate && <div className="date-validation-error">{dateErrors.startDate}</div>}
                </div>
                <div className="filter-item">
                  <label className="filter-label">End Date</label>
                  <DatePicker
                    value={endDate}
                    onChange={handleEndDateChange}
                    placeholder="End Date"
                    className="filter-datepicker"
                  />
                  {dateErrors.endDate && <div className="date-validation-error">{dateErrors.endDate}</div>}
                </div>
                <div className="filter-item">
                  <label className="filter-label">Specialty</label>
                  <MultiSelect
                    name="specialtyDataValue"
                    dataItemKey="id"
                    textField="text"
                    data={specialtyFields.specialtyData}
                    filterable
                    value={specialtyFields.specialtyDataValue}
                    autoClose={false}
                    filter={specialtyFields.filterValue}
                    loading={specialtyFields.isSpecialtyDataLoading}
                    disabled={specialtyFields.isSpecialtyDataLoading}
                    placeholder="Select Specialties"
                    className="filter-multiselect k-multiselect-wrap"
                    onOpen={() =>
                      onOpenChange(
                        setSpecialtyFields,
                        specialtyDataList,
                        "specialtyData",
                        specialtyFields,
                        specialtyFields.specialtyDataValue
                      )
                    }
                    onChange={(e) =>
                      handleChangeDropdownValue(
                        e,
                        "specialtyDataValue",
                        setSpecialtyFields,
                        specialtyDataList,
                        specialtyFields.specialtyDataValue,
                        specialtyFields
                      )
                    }
                    onFilterChange={(e) => filterChange(e, setSpecialtyFields, specialtyDataList, "specialtyData")}
                    onBlur={() => handleBlurDropdown(setSpecialtyFields, "specialtyData")}
                  />
                </div>
                <div className="filter-item">
                  <label className="filter-label">Pay Request Type</label>
                  <MultiSelect
                    name="payRequestTypeDataValue"
                    dataItemKey="id"
                    textField="text"
                    data={payRequestTypeFields.payRequestTypeData}
                    filterable
                    value={payRequestTypeFields.payRequestTypeDataValue}
                    autoClose={false}
                    filter={payRequestTypeFields.filterValue}
                    loading={payRequestTypeFields.isPayRequestTypeDataLoading}
                    disabled={payRequestTypeFields.isPayRequestTypeDataLoading}
                    placeholder="Select Pay Request Types"
                    className="filter-multiselect k-multiselect-wrap"
                    onOpen={() =>
                      onOpenChange(
                        setPayRequestTypeFields,
                        payRequestTypeDataList,
                        "payRequestTypeData",
                        payRequestTypeFields,
                        payRequestTypeFields.payRequestTypeDataValue
                      )
                    }
                    onChange={(e) =>
                      handleChangeDropdownValue(
                        e,
                        "payRequestTypeDataValue",
                        setPayRequestTypeFields,
                        payRequestTypeDataList,
                        payRequestTypeFields.payRequestTypeDataValue,
                        payRequestTypeFields
                      )
                    }
                    onFilterChange={(e) =>
                      filterChange(e, setPayRequestTypeFields, payRequestTypeDataList, "payRequestTypeData")
                    }
                    onBlur={() => handleBlurDropdown(setPayRequestTypeFields, "payRequestTypeData")}
                  />
                </div>
                <div className="filter-item">
                  <label className="filter-label">Provider</label>
                  <MultiSelect
                    name="providerDataValue"
                    dataItemKey="id"
                    textField="text"
                    data={providerFields.providerData}
                    filterable
                    value={providerFields.providerDataValue}
                    autoClose={false}
                    filter={providerFields.filterValue}
                    loading={providerFields.isProviderDataLoading}
                    disabled={providerFields.isProviderDataLoading}
                    placeholder="Select Providers"
                    className="filter-multiselect k-multiselect-wrap"
                    onOpen={() =>
                      onOpenChange(
                        setProviderFields,
                        providerDataList,
                        "providerData",
                        providerFields,
                        providerFields.providerDataValue
                      )
                    }
                    onChange={(e) =>
                      handleChangeDropdownValue(
                        e,
                        "providerDataValue",
                        setProviderFields,
                        providerDataList,
                        providerFields.providerDataValue,
                        providerFields
                      )
                    }
                    onFilterChange={(e) => filterChange(e, setProviderFields, providerDataList, "providerData")}
                    onBlur={() => handleBlurDropdown(setProviderFields, "providerData")}
                  />
                </div>
                <div className="filter-item search-button-container">
                  <label className="filter-label">&nbsp;</label>
                  <button
                    className="search-button"
                    onClick={handleSearchClick}
                    disabled={isLoading || !!dateErrors.startDate || !!dateErrors.endDate}
                    title="Search"
                  >
                    <span className="k-icon k-font-icon k-i-search" />
                  </button>
                </div>
                {hasWriteAccess && (
                  <div className="filter-item action-buttons-container">
                    <label className="filter-label">&nbsp;</label>
                    <div className="action-buttons">
                      <Button
                        className="approve-action-button"
                        disabled={!hasSelectedRows || isSubmitting}
                        onClick={handleApproveClick}
                      >
                        Approve
                      </Button>
                      <Button
                        className="reject-action-button"
                        disabled={!hasSelectedRows || isSubmitting}
                        onClick={handleRejectClick}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {hasSearched ? (
              <div style={{ position: "relative", height: "100%" }}>
                {(isLoading || isSubmitting || isGeneratingTxn) && <LoadingPanel gridId="pay-request-tracker-grid" />}
                {!isLoading && (
                  <DataTable
                    isAPICalling={false}
                    initialLoading={true}
                    data={displayData.map((item) => ({
                      ...item,
                      [SELECTED_FIELD]: selectedState[idGetter(item)],
                    }))}
                    columns={gridColumns}
                    customColumn={customColumn}
                    gridHeight={gridHeight}
                    isPagination={true}
                    isSortable={true}
                    isResizable={true}
                    scrollable="scrollable"
                    id="pay-request-tracker-grid"
                    dataItemKey={DATA_ITEM_KEY}
                    pageData={pageData}
                    dataCount={totalCount}
                    handlePageChange={handlePageChange}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onRowClick={handleRowClick}
                    handleColumnMenu={handleColumnMenu}
                    onClearFilter={onClearFilter}
                    refreshClick={onRefreshClick}
                    onSelectionChange={onSelectionChange}
                    onActionClick={onActionClick}
                    resetSort={resetGrid}
                    isExportDataClick={exportDataClick}
                    setExportData={setExportData}
                    exportData={exportData}
                    fileName="Export_PayRequestTracker"
                    getExportData={getExportData}
                    onRowDoubleClick={handleRowDoubleClick}
                    reorderable={true}
                    columnReorderData={columnReorderData}
                    initialFilter={gridFilter}
                    initialSort={gridSort}
                  />
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  color: "#6B6C6D",
                  fontSize: "14px",
                }}
              >
                {isLoading ? "Loading..." : ""}
              </div>
            )}
          </div>
        </div>
      </div>

      {payRequestDetailedDialogVisible && (
        <PayRequestDetailed
          visible={payRequestDetailedDialogVisible}
          onClose={() => {
            setPayRequestDetailedDialogVisible(false);
            setSelectedTimesheetMstrKey(null);
          }}
          timesheetMstrKey={selectedTimesheetMstrKey}
          onRefresh={onRefreshClick}
          writeAccess={pageProps.writeAccess}
        />
      )}

      {/* Confirmation Dialog for Approve/Reject */}
      <DialogComponent
        title={confirmationPopup.title}
        onCancelConfirm={handleCancelAction}
        width={"800"}
        height={"auto"}
        disabled={isSubmitting}
        className={"Dialog-Approve-Reject"}
        desc={confirmationPopup.desc}
        visible={confirmationPopup.isVisible}
        actionBtn={confirmationPopup.actionBtn}
        titleClassName={confirmationPopup.type === "APPROVE_CONFIRM" ? "approve-confirm" : "reject-confirm"}
        closeIcon={false}
        isCustomDesc={confirmationPopup.isCustomDesc}
        customComponent={confirmationPopup.customComponent}
      />
    </div>
  );
};

export default PayRequestTracker;
